/**
 * Money conversions between what people type and what is stored: integer minor units plus an ISO currency.
 * Parsing and the input rendering use string arithmetic only, so no floating point value ever represents an amount that is stored or compared.
 * `formatMinor` converts to a number for display only.
 */

/** Digits after the decimal separator for the currency (EUR 2, JPY 0, KWD 3), from the platform's currency data. */
export function currencyExponent(currency: string): number {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/** "50", "50.5", "50,50" → minor units. Returns null for anything else (negative, letters, too many decimals, above the storage limit). */
export function parseMajorToMinor(input: string, currency: string, max = 2_000_000_000): number | null {
  const exp = currencyExponent(currency);
  const m = /^(\d{1,12})(?:[.,](\d+))?$/.exec(input.trim());
  if (!m) return null;
  const frac = m[2] ?? '';
  if (frac.length > exp) return null;
  const minor = Number(`${m[1]}${frac.padEnd(exp, '0')}`);
  return Number.isSafeInteger(minor) && minor <= max ? minor : null;
}

/** Minor units → an editable decimal string such as "50.00". */
export function minorToInput(minor: number, currency: string): string {
  const exp = currencyExponent(currency);
  const digits = String(Math.abs(minor)).padStart(exp + 1, '0');
  const whole = digits.slice(0, digits.length - exp);
  return `${minor < 0 ? '-' : ''}${exp ? `${whole}.${digits.slice(digits.length - exp)}` : whole}`;
}

/** Display text such as "€50.00". Display only: never feed the result back into arithmetic. */
export function formatMinor(minor: number, currency: string, locale?: string): string {
  const exp = currencyExponent(currency);
  return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: exp, maximumFractionDigits: exp }).format(Number(minorToInput(minor, currency)));
}
